import { getFromLocalStorage } from 'lib/localStorage';

import RequestError from 'errors/RequestError';

import LINKS from 'constants/links';
import LOCAL_STORAGE from 'constants/localStorage';

const RESPONSE_CODE_FALLBACK = 'UNKNOWN_ERROR';
const RESPONSE_MESSAGE_FALLBACK = 'an unknown error has occurred';

const apiRequest = async ({
  method,
  key,
  useDefaultAPI = true,
  ignoreErrors = false,
  body,
  credentials = null,
  headers = {},
}) => {
  const auth = getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN)
    ? getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN)
    : null;
  const fetchPath = useDefaultAPI
    ? `${process.env.NEXT_PUBLIC_API_URL}${key}`
    : key;
  const currHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...headers,
  };

  // If Content-Type is specified as `null`, let the browser
  // handle this header automatically
  if (headers['Content-Type'] === null) {
    delete currHeaders['Content-Type'];
  }

  if (auth && credentials !== 'include') {
    currHeaders.Authorization = `Bearer ${auth}`;
  }

  let res = null;
  const fetchArgs = { headers: currHeaders, method, body };

  if (credentials) {
    fetchArgs.credentials = credentials;
  }

  try {
    res = await fetch(fetchPath, fetchArgs);
  } catch {
    return null;
  }

  if (res.ok) {
    try {
      const camelCaseKeys = (await import('camelcase-keys')).default;

      return camelCaseKeys(await res.json(), { deep: true });
    } catch {
      return;
    }
  }

  if (ignoreErrors) {
    return null;
  }

  const data = await res.json();

  if ([401, 403, 404].includes(res.status)) {
    const code =
      data?.message ?? data?.errors?.[0].code ?? RESPONSE_CODE_FALLBACK;

    throw new RequestError({ status: res.status, code });
  }

  const errorMessage = data?.message ?? RESPONSE_MESSAGE_FALLBACK;

  if (res.status >= 500 || isValidStatusCodeToTrackErrors(res.status)) {
    const { captureException } = await import('@sentry/nextjs');

    captureException(new Error(errorMessage), {
      contexts: {
        endpoint: key,
        method,
        headers,
      },
    });
  }

  throw new Error(errorMessage);
};

export const del = async (key, options = {}) =>
  apiRequest({ method: 'DELETE', key, ...options });

/**
 * To improve debugging, in specific API responses the error tracking are
 * sended with some useful context data.
 */
export const getAuthTokenFromRequest = (req) =>
  req.headers?.['authorization']?.slice(7) ?? null;

export const get = async (key, options) =>
  apiRequest({ method: 'GET', key, ...options });

const getBodyWithOptions = async (
  body,
  { stringify = true, toSnake = true }
) => {
  let currBody = !body ? null : body;

  if (toSnake) {
    const snakeCaseKeys = (await import('snakecase-keys')).default;

    currBody = snakeCaseKeys(currBody, { deep: true });
  }

  if (stringify) {
    currBody = JSON.stringify(currBody);
  }

  return currBody;
};

export const getFirstPathFromPathname = (pathname) => {
  const regex = /\/[a-z0-9-]+\//i;

  return pathname.length > 1
    ? pathname.match(regex)?.[0] || `${pathname}/`
    : pathname;
};

export const getLastPathPosition = (str = '') =>
  str.includes('/') ? str.lastIndexOf('/') : 0;

const isValidStatusCodeToTrackErrors = (statusCode) =>
  [405, 406, 408, 413, 414, 415].includes(statusCode);

export const patch = async (
  key,
  { body, stringify, toSnake, ...options } = {}
) =>
  apiRequest({
    method: 'PATCH',
    body: !body ? null : await getBodyWithOptions(body, { stringify, toSnake }),
    key,
    ...options,
  });

export const post = async (
  key,
  { body, stringify, toSnake, ...options } = {}
) =>
  apiRequest({
    method: 'POST',
    body: !body ? null : await getBodyWithOptions(body, { stringify, toSnake }),
    key,
    ...options,
  });

export const put = async (key, { body, stringify, toSnake, ...options } = {}) =>
  apiRequest({
    method: 'PUT',
    body: !body ? null : await getBodyWithOptions(body, { stringify, toSnake }),
    key,
    ...options,
  });

const redirectTo404Page = () => ({
  redirect: { destination: LINKS[404], permanent: true },
});

export const redirectTo404IfParamsAreInvalid = (params) => {
  const isValid = params && params.length > 0;

  return isValid ? true : redirectTo404Page();
};

export const removeHashFromUrl = (router) => {
  router.replace(router.asPath.replace(/#(.*)/g, ''));
};

export const stripQs = (str) =>
  str.includes('?') ? str.substring(0, str.indexOf('?')) : str;

export const toEndpointWithOptionalAfterParam = (endpointUrl, query) => {
  const afterParam = query?.after ? query.after : null;

  return afterParam ? `${endpointUrl}&after=${afterParam}` : `${endpointUrl}`;
};
